import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { cn } from '../../lib/cn';

/**
 * THE MONO-IS-A-FACT RULE, as a component.
 *
 * If a number came from the clock, the GPS, an adjustment or payroll, it is
 * Plex Mono with `tabular-nums slashed-zero` — so a column aligns and a ticking
 * timer never re-flows. A count inside a sentence ("3 lignes sélectionnées") is
 * not a fact in this sense and is not a `Readout`.
 *
 * Nothing here ever animates. An elapsed timer re-renders; it does not
 * transition. Animating a payroll fact makes it look computed rather than
 * recorded.
 */
const readout = cva(['num inline-block tabular-nums'], {
  variants: {
    scale: {
      /**
       * 66 px. The punch-screen elapsed number — a pure DURATION. Once per
       * screen, and never for a clock time: "22 h 00" at 66 px and "03 h 12" at
       * 66 px are the same visual gesture at arm's length, which is exactly the
       * ambiguity the home screen must not have.
       */
      duration: 'text-readout',
      /** 40 px. A CLOCK time, a shift window, a hero total. */
      clock: 'text-readout-2',
      /** 25 px. Detail-panel figures, effective hours. */
      detail: 'text-readout-3',
      /** 13 px. Every time, duration, distance and employee number in a grid. */
      data: 'text-data',
    },
    tone: {
      default: 'text-ink',
      muted: 'text-ink-muted',
      /** On a solid status field. */
      onField: 'text-current',
      danger: 'text-status-danger-ink',
    },
    align: { start: 'text-start', end: 'text-end' },
  },
  defaultVariants: { scale: 'data', tone: 'default', align: 'start' },
});

export type ReadoutVariants = VariantProps<typeof readout>;

export interface ReadoutProps
  extends Omit<HTMLAttributes<HTMLElement>, 'children'>,
    ReadoutVariants {
  /** The formatted value — "03 h 12", "21:54", "412 m", "E1052". */
  readonly children: ReactNode;
  /**
   * When set, renders a `<time>` element with this machine-readable value.
   * Every timestamp renders in the SITE's timezone; the caller has already
   * done that conversion.
   */
  readonly dateTime?: string;
  /**
   * The unit or zone, rendered smaller beside the number — "HAE", "km". Kept
   * inside the readout so it never wraps away from the value it qualifies.
   */
  readonly unit?: ReactNode;
  /**
   * A screen-reader-only expansion of the value. "03 h 12" reads as gibberish
   * to a screen reader; "3 heures 12 minutes" does not.
   */
  readonly srValue?: string;
}

export const Readout = forwardRef<HTMLElement, ReadoutProps>(function Readout(
  { scale, tone, align, dateTime, unit, srValue, className, children, ...rest },
  ref,
) {
  const content = (
    <>
      <span aria-hidden={srValue !== undefined ? true : undefined}>{children}</span>
      {srValue !== undefined ? <span className="sr-only">{srValue}</span> : null}
      {unit !== undefined ? (
        <span className="ms-1 font-sans text-support font-normal text-ink-muted">{unit}</span>
      ) : null}
    </>
  );

  if (dateTime !== undefined) {
    return (
      <time
        {...rest}
        ref={ref as React.Ref<HTMLTimeElement>}
        dateTime={dateTime}
        className={cn(readout({ scale, tone, align }), className)}
      >
        {content}
      </time>
    );
  }

  return (
    <span
      {...rest}
      ref={ref as React.Ref<HTMLSpanElement>}
      data-numeric=""
      className={cn(readout({ scale, tone, align }), className)}
    >
      {content}
    </span>
  );
});
